import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import { getCountryCallingCode, type Country } from 'react-phone-number-input';
import FlagIcon from '@/components/FlagIcon';

export interface CountrySelectOption {
    value?: string;
    label: string;
    divider?: boolean;
}

interface CountrySelectWithSearchProps {
    readonly name?: string;
    readonly value?: string;
    readonly onChange: (value: string | undefined) => void;
    readonly onFocus?: () => void;
    readonly onBlur?: () => void;
    readonly options: CountrySelectOption[];
    // iconComponent kept in props signature for library compatibility — replaced internally with SVG flags
    readonly iconComponent?: React.ComponentType<{ country?: string; label?: string; 'aria-hidden'?: boolean }>;
    readonly disabled?: boolean;
    readonly readOnly?: boolean;
    readonly tabIndex?: number;
    readonly className?: string;
    readonly 'aria-label'?: string;
}

const ZERO_WIDTH_SPACE = '\u200B';
const UNFILTERED_LIMIT = 30;

export function CountrySelectWithSearch({
    value,
    onChange,
    onFocus,
    onBlur,
    options,
    disabled = false,
    readOnly = false,
    tabIndex,
    className,
    ...rest
}: CountrySelectWithSearchProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [dropdownRect, setDropdownRect] = useState<{ top: number; left: number; width: number } | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const updateDropdownRect = useCallback(() => {
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            setDropdownRect({
                top: rect.bottom + 6,
                left: rect.left,
                width: Math.max(260, rect.width),
            });
        }
    }, []);

    useEffect(() => {
        if (isOpen && containerRef.current) {
            updateDropdownRect();
            window.addEventListener('scroll', updateDropdownRect, true);
            window.addEventListener('resize', updateDropdownRect);
            return () => {
                window.removeEventListener('scroll', updateDropdownRect, true);
                window.removeEventListener('resize', updateDropdownRect);
            };
        } else {
            setDropdownRect(null);
        }
    }, [isOpen, updateDropdownRect]);

    const selectableOptions = useMemo(
        () => options.filter(o => !o.divider && (o.value || o.value === undefined)),
        [options],
    );

    const filteredOptions = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return selectableOptions.slice(0, UNFILTERED_LIMIT);
        return selectableOptions.filter(
            o =>
                o.label?.toLowerCase().includes(q) ||
                o.value?.toLowerCase().includes(q),
        );
    }, [selectableOptions, search]);

    const selectedOption = useMemo(
        () => selectableOptions.find(o => (o.value ?? 'ZZ') === (value ?? 'ZZ')),
        [selectableOptions, value],
    );

    const dialCode = useMemo(() => {
        if (!value) return null;
        try { return getCountryCallingCode(value as Country); } catch { return null; }
    }, [value]);

    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node;
            if (!containerRef.current?.contains(target) && !dropdownRef.current?.contains(target)) {
                setIsOpen(false);
                onBlur?.();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, onBlur]);

    const isDisabled = disabled || readOnly;

    const handleSelect = (option: CountrySelectOption) => {
        if (option.divider) return;
        onChange(option.value === undefined || option.value === 'ZZ' ? undefined : option.value);
        setIsOpen(false);
        setSearch('');
        onBlur?.();
    };

    const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (!isDisabled) setIsOpen(prev => !prev);
        }
        if (e.key === 'Escape') { setIsOpen(false); setSearch(''); }
    };

    return (
        <div ref={containerRef} className="PhoneInputCountry relative">
            <button
                type="button"
                className={`PhoneInputCountrySelectTrigger ${className ?? ''}`}
                onClick={() => !isDisabled && setIsOpen(prev => !prev)}
                onFocus={onFocus}
                onBlur={() => !isOpen && onBlur?.()}
                onKeyDown={handleTriggerKeyDown}
                disabled={isDisabled}
                tabIndex={tabIndex}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-label={rest['aria-label'] ?? 'Country'}
            >
                {selectedOption && (
                    <span className="PhoneInputCountryIcon flex-shrink-0" aria-hidden>
                        <FlagIcon countryCode={value} size="md" />
                    </span>
                )}
                {dialCode && (
                    <span className="text-sm font-medium text-white/90 tabular-nums leading-none" aria-hidden>
                        +{dialCode}
                    </span>
                )}
                <ChevronDown
                    className={`w-3.5 h-3.5 flex-shrink-0 text-white/70 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    aria-hidden
                />
            </button>

            {isOpen && dropdownRect && createPortal(
                <div
                    ref={dropdownRef}
                    className="PhoneInputCountryDropdown fixed min-w-[260px] max-h-[min(280px,60vh)] flex flex-col rounded-xl border border-white/20 bg-gray-900 shadow-2xl z-[9999]"
                    role="menu"
                    style={{ top: dropdownRect.top, left: dropdownRect.left, width: dropdownRect.width }}
                >
                    <div className="p-2 border-b border-white/10 sticky top-0 bg-gray-900 rounded-t-xl z-10">
                        <input
                            type="text"
                            placeholder="Search country..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === 'Escape') { setSearch(''); (e.target as HTMLInputElement).blur(); }
                            }}
                            className="w-full px-3 py-2 text-sm bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50"
                            autoFocus
                        />
                        {!search.trim() && (
                            <p className="text-xs text-white/40 mt-1 px-1">
                                Showing top {UNFILTERED_LIMIT} — type to search all countries
                            </p>
                        )}
                    </div>
                    <div className="overflow-y-auto overscroll-contain py-1 max-h-[220px]">
                        {filteredOptions.length === 0 ? (
                            <div className="px-3 py-2 text-xs text-white/60">No country found</div>
                        ) : (
                            filteredOptions.map(option => (
                                <button
                                    key={option.value ?? 'ZZ'}
                                    type="button"
                                    role="menuitem"
                                    className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-white hover:bg-white/15 focus:bg-white/15 focus:outline-none ${
                                        (option.value ?? 'ZZ') === (value ?? 'ZZ') ? 'bg-white/20' : ''
                                    }`}
                                    onClick={() => handleSelect(option)}
                                >
                                    <span className="flex h-[18px] w-[27px] flex-shrink-0 items-center justify-center overflow-hidden rounded-[2px]">
                                        <FlagIcon countryCode={option.value} size="md" />
                                    </span>
                                    <span className="truncate">{option.label || ZERO_WIDTH_SPACE}</span>
                                </button>
                            ))
                        )}
                    </div>
                </div>,
                document.body,
            )}
        </div>
    );
}
